import { Icon } from '@chakra-ui/react';
import { Box, ChakraSelect, HStack, IconButtonWithTooltip, Text } from '@elkaso-app/web-design';
import { useGetSupplierProductsApi } from 'apis/supplier';
import { EditProductInfoModal } from 'pages/exclusive-deals/components/edit-product-info-modal';
import { useMemo } from 'react';
import { FiEdit, FiPlus } from 'react-icons/fi';
import type { ProductOption } from './editable-deal-row.types';

interface Props {
  supplierId: number | null;
  dealId?: number;
  value: ProductOption | null;
  isInvalid?: boolean;
  onChange: (product: ProductOption | null) => void;
}

export const ProductSelectCell = ({ supplierId, dealId, value, isInvalid, onChange }: Props) => {
  const { data, isLoading } = useGetSupplierProductsApi(supplierId, undefined, { enabled: !!supplierId });

  // The products list endpoint carries specs in the `description` field; map it to specs (display-only).
  // `label` appends brand + origin so suppliers with several near-identical products (e.g. five
  // "Chicken Breast" SKUs) are distinguishable in the dropdown. `nameEn` keeps the bare product
  // name for storage/captions — the combined label must not leak into the saved name.
  // Field mapping is defensive across response shapes (unit.abbreviation vs defaultUnit, etc.)
  // so the readonly grid columns autofill from whatever the endpoint provides.
  const options = useMemo(
    () =>
      (data?.data ?? []).map((p) => {
        const nameEn = p.name?.en || `Product #${p.id}`;
        const label = [nameEn, p.brand?.en, p.origin].filter(Boolean).join(' · ');
        return {
          value: p.id,
          label,
          nameEn,
          brandEn: p.brand?.en ?? '',
          defaultUnit: p.defaultUnit ?? p.unit?.abbreviation ?? '',
          specs: p.specs ?? p.description ?? '',
          origin: p.origin ?? '',
          state: p.state ?? '',
          catalogCategoryId: p.catalogCategoryId ?? p.category?.id ?? null,
          categoryTitle: p.categoryTitle ?? p.category?.title ?? '',
          imageUrl: p.imageUrl ?? '',
        };
      }),
    [data]
  );

  const selected = value ? { value: value.id, label: value.nameEn } : null;

  return (
    <Box minW='240px'>
      <HStack spacing='1' align='center'>
        <Box flex='1'>
          <ChakraSelect
            size='sm'
            options={options}
            value={selected}
            isLoading={isLoading}
            isInvalid={isInvalid}
            isDisabled={!supplierId}
            placeholder={supplierId ? 'Product' : 'Pick supplier'}
            // Portal the menu to body so the grid's scroll/overflow container can't clip it.
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            onChange={(opt: any) =>
              onChange(
                opt
                  ? {
                      id: opt.value,
                      nameEn: opt.nameEn ?? opt.label,
                      brandEn: opt.brandEn ?? '',
                      defaultUnit: opt.defaultUnit ?? '',
                      specs: opt.specs ?? '',
                      origin: opt.origin ?? '',
                      state: opt.state ?? '',
                      catalogCategoryId: opt.catalogCategoryId ?? null,
                      categoryTitle: opt.categoryTitle ?? '',
                      imageUrl: opt.imageUrl ?? '',
                    }
                  : null
              )
            }
          />
        </Box>
        {supplierId && (
          <EditProductInfoModal
            variant='create'
            supplierId={supplierId}
            onCreated={(product) => onChange(product)}
            trigger={
              <IconButtonWithTooltip
                size='sm'
                variant='outline'
                label='Create new product'
                aria-label='create-product-btn'
                Icon={<Icon as={FiPlus} />}
              />
            }
          />
        )}
        {supplierId && value && (
          // Deal-backed rows edit via the deal-scoped endpoints; new rows (no deal yet —
          // e.g. right after inline product creation) edit via the product-addressed one.
          <EditProductInfoModal
            variant='edit'
            deal={dealId ? { id: dealId } : undefined}
            supplierId={supplierId}
            product={value}
            onUpdated={(fields) => onChange({ ...value, ...fields })}
            trigger={
              <IconButtonWithTooltip
                size='sm'
                variant='outline'
                label='Edit product'
                aria-label='edit-product-btn'
                Icon={<Icon as={FiEdit} />}
              />
            }
          />
        )}
      </HStack>
      {value && value.nameEn && (
        // Full product name caption: the select truncates long names with no tooltip, so we
        // surface the complete name here (wraps, never clipped). Mirrors SupplierSelectCell.
        <Text fontSize='xs' color='gray.600' lineHeight='short' mt='1' whiteSpace='normal'>
          {value.nameEn}
        </Text>
      )}
    </Box>
  );
};
